import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { QuantityText } from '@plato/ui-legacy/components/typography/quantity-text';
import { toCurrencyStringOrEmpty } from '@plato/ui-legacy/lib/currency-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import type { ColumnDef } from '@plato/ui/components/data-table';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { GetManyQuotesOrdersListContaingProductListItem } from '../../../server/types/infer-trpc-types.ts';
import { QuoteLink } from '../../quote-order-detail/components/quote-order-link.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';

export function useQuoteColumnsForProductDetail() {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const customerViewToggled = useIsCustomerMode();
    const { canReadGrossMargin } = useAppConfig();

    const quoteColumnsForProductDetail: ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'customer_name',
                header: t('table_header__customer', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row, cell }) => (
                    <Link
                        className="font-medium hover:underline"
                        to={`/customers/${encodeURIComponent(cell.row.original.customer_id ?? '')}`}
                    >
                        <span className="text-blue-700">{row.original.customer_id}</span>
                        {` ${cell.renderValue() /* eslint-disable-line @typescript-eslint/restrict-template-expressions -- legacy code */}`}
                    </Link>
                ),
                size: 250,
                meta: { pinned: 'left' },
            },
            {
                accessorKey: 'order_id',
                header: t('table_header__quote_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <QuoteLink transactionId={row.original.order_id} />,
            },
            {
                accessorKey: 'status',
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <OrderStatusBadge status={row.original.status} type={SrSalesOrderType.QUOTE} />
                ),
            },
            {
                accessorKey: 'entry_date',
                header: t('table_header__quote_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <DateText date={row.original.entry_date} />,
            },
            {
                accessorKey: 'item_quantity_in_order',
                header: t('table_header__quantity', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <QuantityText
                        quantity={row.original.item_quantity_in_order}
                        unit={row.original.quantity_unit_sales}
                    />
                ),
            },

            {
                accessorKey: 'product_unit_price',
                header: t('table_header__price', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_unit_price, locale),
            },
            {
                accessorKey: 'product_total_in_order',
                header: () => (
                    <span className="flex items-center gap-tight">
                        {t('table_header__total', { ns: 'quotes-orders-list' })}
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent>
                                <InfoHintHeader>
                                    <InfoHintTitle>
                                        {t('table_header__total', { ns: 'quotes-orders-list' })}
                                    </InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>
                                    {t('table_header__total_tooltip', { ns: 'product-details' })}
                                </InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </span>
                ),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_total_in_order, locale),
            },
            {
                accessorKey: 'product_gross_margin_in_order',
                meta: {
                    enabled: !customerViewToggled && canReadGrossMargin,
                    aiChat: {
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: t('table_header__margin', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) =>
                    toCurrencyStringOrEmpty(row.original.product_gross_margin_in_order, locale),
            },
            {
                accessorKey: 'product_gross_margin_percent',
                meta: {
                    enabled: !customerViewToggled && canReadGrossMargin,
                    aiChat: {
                        description: 'Gross margin percentage of this product within the quote',
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: t('table_header__margin_percent', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <MarginPercentage
                            margin={row.original.product_gross_margin_in_order}
                            revenue={row.original.product_total_in_order}
                        />
                    </DataText>
                ),
            },
        ],
        [t, canReadGrossMargin, customerViewToggled, locale],
    );

    return quoteColumnsForProductDetail;
}
